import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { useSelectorUserInfo } from '@/components/auth/AuthContext';
import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import InfinitySelect from '@/components/common/ui/select';
import { TextareaCustomForm } from '@/components/common/ui/textarea';
import UploadImage from '@/components/common/ui/upload/uploadImage';
import { IconArrow } from '@/components/icons';
import { useTranslation } from '@/config/i18n';
import useScrollToError from '@/hooks/useScrollToError';
import {
  getValueFromSelect,
  normalizeTimeZone,
  toastErrorMessage,
  toastMessage,
} from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import {
  HTTP_STATUS,
  MAX_LENGTH,
  UNIPAY_APP_STORE_ID,
} from '@/shared/constants';
import {
  PAYMENT_STATUS,
  ROLE,
  STAGE_GRADUATION_STATUS,
} from '@/shared/constants/user';
import { profileFormSchema, ProfileFormType } from '@/shared/schema/profile';
import { GraduationTermPayload, GraduationType } from '@/types/graduation';
import { HistoryChangePlanType } from '@/types/plan';
import PopupHistoryChangePlan from './PopupHistoryChangePlan';
import { PAYMENT_TYPE } from '@/shared/constants/payment';
import { useUnivaPayTokenMethod } from '@/hooks';

const ProfileForm: React.FC = () => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const navigate = useNavigate();
  const { apiService } = apiRequest();
  const [isActive, setIsActive] = useState(true);
  const [originalActive, setOriginalActive] = useState(false);
  const [defaultImage, setDefaultImage] = useState<string>();
  const [graduations, setGraduations] = useState<GraduationType[]>([]);
  const [historyChangePlan, setHistoryChangePlan] = useState<
    HistoryChangePlanType[]
  >([]);
  const [visibleModalHistoryChangePlan, setVisibleModalHistoryChangePlan] =
    useState(false);

  const { startCheckout } = useUnivaPayTokenMethod();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const { currentUser } = useSelectorUserInfo();
  const {
    current_roles,
    id,
    plan_type,
    plan: currentPlan,
    stage_graduation_status,
    payment_type,
    email,
    phone_number,
  } = currentUser;
  const userRole = current_roles?.[0] || '';
  const isCoachCharge =
    userRole === ROLE.TEACHER && plan_type !== PAYMENT_STATUS.FREE;
  const isCoachTrial =
    userRole === ROLE.TEACHER &&
    stage_graduation_status === STAGE_GRADUATION_STATUS.TRIAL;
  const isCoachFree =
    userRole === ROLE.TEACHER &&
    stage_graduation_status === STAGE_GRADUATION_STATUS.FREE;

  const formMethods = useForm<ProfileFormType>({
    mode: 'onChange',
    resolver: yupResolver(profileFormSchema),
    shouldFocusError: false,
    defaultValues: {
      require_password_update: true,
      email: '',
      password: '',
      password_confirmation: '',
      first_name_kanji: '',
      last_name_kanji: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      phone_number: '',
      post_code: '',
      address: '',
      student_limit: 10,
      plan: null,
      graduation_terms: null as any,
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = formMethods;

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  const getGraduationList = useCallback(async () => {
    const { data } = await apiService.guest.collection('graduations', {
      method: 'GET',
      params: {
        timezone: normalizeTimeZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
        ignore_status: 'CLOSED',
      },
    });

    setGraduations(data?.data || []);
  }, []);

  const getPlanChangeHistoryList = useCallback(async () => {
    const { data } = await apiService.users.collection(
      `${id}/history-change-plans`,
      {
        method: 'GET',
      },
    );

    setHistoryChangePlan(data?.data || []);
  }, []);

  async function getUserDetail() {
    setLoadingGlobal(true);
    const user = currentUser;

    if (user) {
      setDefaultValue(user);
      setDefaultImage(user.profile_image || '');
    }
    setLoadingGlobal(false);
  }

  function setDefaultValue(data: any) {
    const specialField = [
      'gender',
      'date_of_birth',
      'status',
      'require_password_update',
      'graduation_terms',
    ];
    getAllFieldNames().forEach((field: any) => {
      const val =
        field === 'graduation_terms'
          ? data.graduation_terms ?? data.graduation_term
          : data[field];

      if (val && !specialField.includes(field)) {
        setValue(field, val);
      }

      if (field === 'graduation_terms') {
        if (val == null || val === '') {
          return;
        }
        if (typeof val === 'object' && val !== null && 'name' in val) {
          const term = val as GraduationTermPayload;
          const period = data.graduation?.period_number;
          const labelText =
            period != null ? `${period}期ー${term.name}` : String(term.name);
          setValue(field, {
            label: labelText,
            value: period ?? term.id,
          });
        } else {
          setValue(field, {
            label: String(val),
            value: val,
          });
        }
      }

      if (field === 'gender') {
        setValue(field, { label: t(`common.gender.${val}`), value: val });
      }

      if (field === 'status') {
        const status = data.status === 'active' ? true : false;
        setValue(field, status);
        setIsActive(status);
        setOriginalActive(status);
      }

      if (field === 'date_of_birth') {
        if (val) {
          setValue(field, new Date(val));
        }
      }

      if (field === 'require_password_update') {
        setValue(field, false);
      }

      if (field === 'plan') {
        if (val) {
          setValue(field, val.id);
        }
      }
    });
  }

  useEffect(() => {
    getUserDetail();
  }, [id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  async function onSubmit() {
    setLoadingGlobal(true);
    const payload = getPayload();
    let data = null;
    let error = null;
    if (id) {
      const { data: user, error: userError } = await apiService.users.update(
        id,
        payload,
      );
      data = user;
      error = userError;
    } else {
      const { data: user, error: userError } =
        await apiService.users.create(payload);
      data = user;
      error = userError;
    }
    if (error) {
      handleError(error);
      setLoadingGlobal(false);
      return;
    }

    if (data) {
      let msg = t('profile.coach_success');
      if (id) {
        msg = t('profile.update_coach_success');
      }
      toast?.current?.show(toastMessage(msg));
      setLoadingGlobal(false);
      window.location.reload();
    }
  }

  function getPayload() {
    const status = isActive ? 'active' : 'inactive';
    const {
      date_of_birth,
      password,
      password_confirmation,
      student_limit,
      ...rest
    } = getValues();
    const valuesSelect = getValueFromSelect(rest, [
      'gender',
      'graduation_terms',
    ]);
    const dateOfBirth = date_of_birth
      ? dayjs(date_of_birth.toString()).format('YYYY-MM-DD')
      : '';
    const passwordCustom = !password ? undefined : password;
    const passwordConfirmationCustom = !password_confirmation
      ? undefined
      : password_confirmation;

    const statusParams = id
      ? isActive === originalActive
        ? undefined
        : status
      : status;

    return {
      ...rest,
      ...valuesSelect,
      status: statusParams,
      date_of_birth: dateOfBirth,
      password: passwordCustom,
      password_confirmation: passwordConfirmationCustom,
      student_limit: Number(student_limit),
    };
  }

  function handleError(error: any) {
    const { status_code, errors } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field, message } = e;
        setError(field, { message });
      });
      return;
    }

    const msg = t('profile.coach_fail');
    toast?.current?.show(toastErrorMessage(msg));
  }

  const onBeforeUploadAvatar = (event: any) => {
    const MAX_SIZE_MB = 20;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const file = event.files[0];
    if (file.size > MAX_SIZE_BYTES) {
      event.options.clear(); // clear the files from the upload
      toast?.current?.show(toastErrorMessage(t('common.file.image_max_20MB')));
      return;
    }
  };

  const handleRegisterCard = async () => {
    setTimeout(() => {
      startCheckout(
        'アートシフトトレーニングアプリ',
        'アートシフトトレーニングアプリ',
        '支払い情報の変更　　　　　　　　　　　　ご利用中のカード情報を新しいカードに変更できます。',
        email,
        phone_number as string,
        async (tokenId: string) => {
          const { error } = await apiService.recurring.collection(
            'change-card',
            {
              method: 'PUT',
              body: {
                recurring_token_id: tokenId,
              },
            },
          );

          if (error) {
            toast?.current?.show(toastErrorMessage(error.message));
            return;
          }

          toast?.current?.show(
            toastMessage(t('profile.register_card_success')),
          );
        },
      );
    }, 500);
  };

  useEffect(() => {
    getGraduationList();
  }, [getGraduationList]);

  useEffect(() => {
    if (isCoachCharge) {
      getPlanChangeHistoryList();
    }
  }, [isCoachCharge, getPlanChangeHistoryList]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px]"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.avatar')}</span>
            </div>
            <div className="w-[512px] flex">
              <UploadImage
                idChooseBtn="choose-image"
                className="relative w-[366px] h-[126px] flex-full-center border border-gray-200"
                name="profile_image_id"
                accept=".svg,.jpg,.jpeg,.png"
                defaultUrl={defaultImage}
                onBeforeUpload={onBeforeUploadAvatar}
                maxSize={5}
                handleUploaded={(id) => {
                  setValue('profile_image_id', id);
                }}
                textUpload="ファイルを選択"
                textSubUpload="またはここにファイルをドロップ"
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.email')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="email"
              wrapClass="w-[512px]"
              disabled={!!id}
            />
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.phone_number')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                name="phone_number"
                wrapClass="w-full"
                maxLength={MAX_LENGTH.PHONE_NUMBER}
                isNumber
              />
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('profile.phone_hint')}
              </p>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.password')}</span>
              {!id && <span className="text-error-500">*</span>}
            </div>
            <div className="w-[512px]">
              <div className="grid grid-cols-2 gap-6">
                <InputTextCustomForm
                  name="password"
                  type="text"
                  isHideValue={true}
                  placeholder={t('profile.password')}
                  maxLength={MAX_LENGTH.PASSWORD}
                />
                <InputTextCustomForm
                  name="password_confirmation"
                  type="text"
                  isHideValue={true}
                  placeholder={t('profile.password_confirm')}
                  maxLength={MAX_LENGTH.PASSWORD}
                />
              </div>
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('profile.password_hint')}
              </p>
            </div>
          </div>

          <div className="break-line border-t border-gray-200 h-[1px] mt-[20px]"></div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.kanji_name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <InputTextCustomForm
                name="last_name_kanji"
                placeholder={t('profile.last_name_kanji_placeholder')}
              />
              <InputTextCustomForm
                name="first_name_kanji"
                placeholder={t('profile.first_name_kanji_placeholder')}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <InputTextCustomForm
                name="last_name"
                placeholder={t('profile.last_name_placeholder')}
              />
              <InputTextCustomForm
                name="first_name"
                placeholder={t('profile.first_name_placeholder')}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.graduation_terms')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InfinitySelect
              placeholder=""
              wrapClass="w-[512px]"
              name="graduation_terms"
              defaultOption={graduations.map((item: GraduationType) => ({
                label: item.period_number,
                value: item.period_number,
              }))}
              focusDefaultOption={false}
              onMenuOpen={() => {
                setTimeout(() => {
                  const selectedEl = document.getElementsByClassName(
                    'select__option--is-selected',
                  )[0];
                  if (selectedEl) {
                    selectedEl.scrollIntoView({});
                  }
                }, 15);
              }}
              isDisable
            />
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.date_of_birth')}</span>
            </div>
            <div className="w-[512px]">
              <CalendarCustomForm
                wrapClass="w-full"
                name="date_of_birth"
                dateFormat="yy/mm/dd"
                maxDate={new Date()}
                showIcon
                icon={() => <i className="pi pi-calendar text-[18px]" />}
                placeholder="yyyy/mm/dd"
                panelClassName="calendar-date-of-birth"
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.gender')}</span>
            </div>
            <div className="w-[512px]">
              <InfinitySelect
                name="gender"
                placeholder=""
                wrapClass="w-[512px]"
                isClearable={false}
                defaultOption={[
                  { value: 'male', label: t('common.gender.male') },
                  { value: 'female', label: t('common.gender.female') },
                  { value: 'other', label: t('common.gender.other') },
                ]}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.post_code')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                wrapClass="w-full"
                name="post_code"
                placeholder="XXXXXXX"
                maxLength={MAX_LENGTH.POSTCODE}
              />
              <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
                {t('profile.postcode_hint')}
              </p>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('profile.address')}</span>
              <span className="text-error-500">*</span>
            </div>
            <TextareaCustomForm
              name="address"
              wrapClass="w-[512px]"
              maxLength={MAX_LENGTH.ADDRESS}
            />
          </div>

          {!isCoachCharge && (
            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('profile.student_limit')}</span>
                <span className="text-error-500">*</span>
              </div>
              <div className="w-[512px]">
                <InputTextCustomForm
                  wrapClass="w-full"
                  name="student_limit"
                  isNumber
                />
              </div>
            </div>
          )}

          {isCoachCharge && (
            <>
              <div className="form-item flex mt-[20px]">
                <div className="label w-56 flex">
                  <span>{t('profile.plan')}</span>
                </div>
                <div className="w-[512px] p-4 bg-gray-100 rounded-lg">
                  <div
                    className={classNames(
                      'px-[10px] py-[14px] border border-gray-300 bg-white rounded-lg opacity-70 overflow-hidden relative cursor-pointer flex items-center',
                      {
                        '!cursor-not-allowed !bg-gray-100':
                          isCoachFree || isCoachTrial,
                      },
                    )}
                    onClick={() => {
                      if (isCoachFree || isCoachTrial) return;
                      navigate(PATH.profile_change_plan);
                    }}
                  >
                    {currentPlan ? (
                      <div className="w-[440px]">
                        <span className="font-bold">{`${currentPlan.name} - ${currentPlan.price.toLocaleString()}円/`}</span>
                        {`月（税込）・受講生管理可能人数 ${currentPlan.student_limit}人`}
                      </div>
                    ) : (
                      <span className="text-gray-500">
                        {t('common.form.empty')}
                      </span>
                    )}
                    <IconArrow />
                  </div>
                  <div
                    className="text-primary-500 text-md font-normal flex items-center cursor-pointer mt-4 justify-end"
                    onClick={() => setVisibleModalHistoryChangePlan(true)}
                  >
                    プラン変更履歴
                    <IconArrow />
                  </div>
                </div>
              </div>

              <div className="text-md font-normal flex items-center mt-4">
                ※カード情報を変更したい場合、
                {payment_type !== PAYMENT_TYPE.RECURRING && (
                  <Link
                    className="text-primary-700 cursor-pointer"
                    to={`https://customer.univapay.com/stores/${UNIPAY_APP_STORE_ID}`}
                    target="_blank"
                  >
                    こちら
                  </Link>
                )}
                {payment_type === PAYMENT_TYPE.RECURRING && (
                  <div
                    className="text-primary-700 cursor-pointer"
                    onClick={handleRegisterCard}
                  >
                    こちら
                  </div>
                )}
                から変更可能です。
              </div>
            </>
          )}

          <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px]">
            <ButtonCustom
              className="ml-2 w-[140px]"
              type="submit"
              loading={loadingGlobal}
            >
              {id ? t('common.form.save') : t('common.form.new')}
            </ButtonCustom>
          </div>
        </form>
      </FormProvider>

      <PopupHistoryChangePlan
        data={historyChangePlan}
        isOpen={visibleModalHistoryChangePlan}
        onClose={() => setVisibleModalHistoryChangePlan(false)}
      />
    </>
  );
};

export default ProfileForm;
